// Mouse Interaction System for Scene Editor
import { System } from '@/engine/systems/System';
import { eventBus } from '@/engine/events/EventBus';
import { logger } from '@/engine/logging';
import { editorMode, selectObject, setViewport, sceneState } from '../stores/sceneEditorStore';
import * as PIXI from 'pixi.js';

export class MouseInteractionSystem extends System {
  private viewport?: PIXI.Container;
  private gizmoContainer?: PIXI.Container;
  private findObjectAtPosition?: (x: number, y: number) => any;

  // Mouse interaction state
  private isDragging = false;
  private dragStartPos = { x: 0, y: 0 };
  private globalMouseCleanup: (() => void) | null = null;

  constructor(scene: any) {
    super(scene);
  }

  /**
   * Инициализация системы с необходимыми зависимостями
   */
  initialize(
    viewport: PIXI.Container,
    gizmoContainer: PIXI.Container,
    findObjectAtPosition: (x: number, y: number) => any
  ) {
    this.viewport = viewport;
    this.gizmoContainer = gizmoContainer;
    this.findObjectAtPosition = findObjectAtPosition;

    this.setupGlobalMouseEvents();

    logger.info('🖱️ Mouse Interaction System initialized', {
      source: 'mouse-interaction-system',
      viewport: !!this.viewport,
      gizmoContainer: !!this.gizmoContainer,
    });
  }

  protected onStart(): void {
    // Система уже инициализирована в initialize()
  }

  protected onStop(): void {
    this.cleanup();
  }

  protected onUpdate(_deltaTime: number): void {
    // Обновление не требуется для мышиных событий
  }

  protected onDestroy(): void {
    this.cleanup();
  }

  private cleanup() {
    this.globalMouseCleanup?.();
    this.globalMouseCleanup = null;
  }

  private setupGlobalMouseEvents() {
    // Используем глобальные события из EventBus через DOMEventBridge
    const canvas = document.getElementById('pixi-canvas') as HTMLCanvasElement;
    if (!canvas) {
      logger.warn('⚠️ Canvas not found for coordinate conversion', {
        source: 'mouse-interaction-system',
      });
      return;
    }

    // Обработчики для глобальных событий
    const handleMouseDown = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Преобразуем в локальные координаты viewport
      const localPos = this.viewport!.toLocal({ x, y });

      this.onGlobalPointerDown(event, localPos);
    };

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const localPos = this.viewport!.toLocal({ x, y });

      this.onGlobalPointerMove(event, localPos);
    };

    const handleMouseUp = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const localPos = this.viewport!.toLocal({ x, y });

      this.onGlobalPointerUp(event, localPos);
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      const localPos = this.viewport!.toLocal({ x, y });

      this.onWheel({
        deltaY: event.deltaY,
        preventDefault: () => event.preventDefault(),
        global: { x, y },
        local: localPos,
      } as any);
    };

    // Подписываемся на глобальные события через EventBus
    eventBus.on('mousedown', handleMouseDown);
    eventBus.on('mousemove', handleMouseMove);
    eventBus.on('mouseup', handleMouseUp);
    eventBus.on('wheel', handleWheel);

    // Сохраняем ссылки для очистки
    this.globalMouseCleanup = () => {
      eventBus.off('mousedown', handleMouseDown);
      eventBus.off('mousemove', handleMouseMove);
      eventBus.off('mouseup', handleMouseUp);
      eventBus.off('wheel', handleWheel);
    };
  }

  private onGlobalPointerDown(event: MouseEvent, localPos: PIXI.Point) {
    if (!this.viewport) {
      logger.warn('⚠️ Viewport not available', { source: 'mouse-interaction-system' });
      return;
    }

    // ВАЖНО: Проверяем, что клик был именно по canvas, а не по UI элементам
    const canvas = document.getElementById('pixi-canvas') as HTMLCanvasElement;
    if (!canvas || event.target !== canvas) {
      logger.info('🖱️ Click outside canvas - ignoring', {
        source: 'mouse-interaction-system',
        target: (event.target as any)?.tagName,
        canvas: !!canvas,
      });
      return;
    }

    const currentMode = editorMode.get();

    logger.info('🖱️ Global pointer down received', {
      source: 'mouse-interaction-system',
      localPos,
      currentMode,
      button: event.button,
      viewportInteractive: this.viewport.interactive,
    });

    // Handle pan mode or middle mouse button
    if (currentMode === 'pan' || event.button === 1) {
      // 1 = middle mouse button
      this.isDragging = true;
      // Сохраняем начальную позицию viewport для корректного pan
      this.dragStartPos = {
        x: sceneState.get().viewport.x,
        y: sceneState.get().viewport.y,
      };
      logger.info('🖱️ Pan mode - dragging started', {
        source: 'mouse-interaction-system',
        viewportStart: this.dragStartPos,
        mode: currentMode,
        button: event.button,
      });
      return;
    }

    // В режимах трансформации gizmo сам обрабатывает события через PIXI
    // НЕ устанавливаем isDragging чтобы не блокировать gizmo

    // Find object under cursor - для выбора объектов во ВСЕХ режимах редактирования
    // Gizmo handles имеют приоритет (stopPropagation), поэтому это срабатывает только при клике на объект
    if (this.findObjectAtPosition) {
      const hitObject = this.findObjectAtPosition(localPos.x, localPos.y);

      if (hitObject) {
        // Выбираем объект только если он еще не выбран (чтобы не сбрасывать drag)
        const state = sceneState.get();
        if (state.selectedObjectId !== hitObject.id) {
          selectObject(hitObject.id);
          logger.info('🎯 Object selected', {
            source: 'mouse-interaction-system',
            objectId: hitObject.id,
            mode: currentMode,
          });
        }
      } else {
        // Сбрасываем выбор только в режиме select
        if (currentMode === 'select') {
          selectObject(null);
        }
      }
    }

    logger.info('🖱️ Global pointer down', {
      source: 'mouse-interaction-system',
      mode: currentMode,
      position: localPos,
      hitObject: this.findObjectAtPosition
        ? this.findObjectAtPosition(localPos.x, localPos.y)?.id || null
        : null,
      isDragging: this.isDragging,
    });
  }

  private onGlobalPointerMove(_event: MouseEvent, localPos: PIXI.Point) {
    if (!this.isDragging || !this.viewport) return;

    const currentMode = editorMode.get();

    // Handle pan mode
    if (currentMode === 'pan') {
      // ВАЖНО: localPos уже в world координатах viewport, используем напрямую
      const state = sceneState.get();

      // Вычисляем новую позицию viewport относительно начальной
      // dragStartPos хранит начальное состояние viewport (x, y)
      const newX = this.dragStartPos.x + localPos.x;
      const newY = this.dragStartPos.y + localPos.y;

      // Update through store
      setViewport(newX, newY, state.viewport.zoom);

      logger.info('🖱️ Pan mode - viewport moved', {
        source: 'mouse-interaction-system',
        localPos,
        newPosition: { x: newX, y: newY },
        dragStart: this.dragStartPos,
      });
      return;
    }

    // В режимах трансформации gizmo сам обрабатывает события через PIXI
  }

  private onGlobalPointerUp(_event: MouseEvent, localPos: PIXI.Point) {
    this.isDragging = false;
    const currentMode = editorMode.get();

    // В режимах трансформации gizmo сам обрабатывает события через PIXI

    logger.info('🖱️ Global pointer up', {
      source: 'mouse-interaction-system',
      mode: currentMode,
      position: localPos,
      isDragging: this.isDragging,
    });
  }

  private onWheel(event: any) {
    event.preventDefault();

    const state = sceneState.get();
    const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(5, state.viewport.zoom * zoomFactor));

    setViewport(state.viewport.x, state.viewport.y, newZoom);

    logger.info('🔍 Zoom changed', {
      source: 'mouse-interaction-system',
      oldZoom: state.viewport.zoom,
      newZoom,
      deltaY: event.deltaY,
    });
  }
}
